import { classNames } from "primereact/utils";
import { AppDialog } from "../../../AppDialog/AppDialog";
import { useLastWhileVisible } from "../../../AppDialog/hooks/useLastWhileVisible";
import { DefaultButton } from "../../../button/defaultButton/DefaultButton";
import { RenderWhen } from "../../../../infrastructure/components/RenderWhen";
import { dateUtility } from "../../../../infrastructure/utilities/dateUtility";
import { BodyMetricType } from "../../../../enums";
import { BodyMetricTypeLabels } from "../../../../labels";
import { BodyMetricIcon } from "../../BodyMetricIcon/BodyMetricIcon";
import { IBodyMetricSummary, IBodyMetricsDataSource } from "../../models/bodyMetricModels";
import { BODY_METRIC_TYPES } from "../../models/bodyMetricTypes";
import { BODY_METRIC_RANGES, useBodyMetricDetails } from "../../hooks/useBodyMetricDetails";
import {
  formatBodyMetricChange,
  formatBodyMetricNumber,
  formatBodyMetricValue,
} from "../../utils/bodyMetricsUtility";
import { BodyMetricChangesChart, BodyMetricTrendChart } from "./BodyMetricCharts";
import styles from "./BodyMetricDetailDialog.module.scss";

interface IBodyMetricDetailDialogProps {
  summary: IBodyMetricSummary | null;
  dataSource: IBodyMetricsDataSource;
  onHide: () => void;
  onAdd: (type: BodyMetricType) => void;
}

/** One metric in depth: the trend over time, its changes, the key numbers and every reading. */
export const BodyMetricDetailDialog = ({
  summary,
  dataSource,
  onHide,
  onAdd,
}: IBodyMetricDetailDialogProps) => {
  // The metric stays on screen (with its colours) while the dialog animates out; it
  // closes when the caller clears it, or when its last reading is deleted.
  const visible = !!summary;
  const shown = useLastWhileVisible(visible, summary);
  const { range, setRange, inRange, stats, changes, readings, requestDelete } =
    useBodyMetricDetails(shown, dataSource, visible);

  if (!shown) return null;

  const { type, latest } = shown;
  const config = BODY_METRIC_TYPES[type];

  return (
    <AppDialog
      visible={visible}
      onHide={onHide}
      className={classNames(styles.root, styles[`root_${config.group}`])}
      header={
        <span className={styles.root_header}>
          <span className={styles.root_orb}>
            <BodyMetricIcon type={type} size={20} />
          </span>
          {BodyMetricTypeLabels[type]}
        </span>
      }
    >
      <div className={styles.root_content}>
        <div className={styles.root_hero}>
          <div>
            <div className={styles.root_hero_value}>
              {formatBodyMetricNumber(type, latest.value)}
              <span className={styles.root_hero_unit}>{config.unit}</span>
            </div>
            <div className={styles.root_hero_date}>
              последно на {dateUtility.formatDate(latest.measuredOn)}
            </div>
          </div>
          <DefaultButton
            design="outline"
            label="+ Добави"
            width="auto"
            className={styles.root_hero_add}
            onClick={() => onAdd(type)}
          />
        </div>

        <div className={styles.root_ranges} role="tablist" aria-label="Период">
          {BODY_METRIC_RANGES.map((option) => (
            <button
              key={option.value}
              type="button"
              role="tab"
              aria-selected={range === option.value}
              className={classNames(styles.root_ranges_chip, {
                [styles.root_ranges_chip_active]: range === option.value,
              })}
              onClick={() => setRange(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>

        <RenderWhen condition={!inRange.length}>
          <p className={styles.root_hint}>Няма стойности за този период.</p>
        </RenderWhen>

        <RenderWhen condition={inRange.length > 0}>
          <section className={styles.root_card}>
            <h3 className={styles.root_card_title}>Прогрес</h3>
            <div className={styles.root_card_canvas}>
              <BodyMetricTrendChart type={type} readings={inRange} />
            </div>
            <RenderWhen condition={inRange.length < 2}>
              <p className={styles.root_hint}>Добавете още стойности, за да видите тенденцията.</p>
            </RenderWhen>
          </section>

          <div className={styles.root_tiles}>
            <div className={styles.root_tile}>
              <span className={styles.root_tile_label}>Начало</span>
              <span className={styles.root_tile_value}>
                {stats ? formatBodyMetricValue(type, stats.first) : ""}
              </span>
            </div>
            <div className={styles.root_tile}>
              <span className={styles.root_tile_label}>Промяна</span>
              <span className={classNames(styles.root_tile_value, styles.root_tile_value_accent)}>
                {stats ? formatBodyMetricChange(type, stats.change) : ""}
              </span>
            </div>
            <div className={styles.root_tile}>
              <span className={styles.root_tile_label}>Най-ниско</span>
              <span className={styles.root_tile_value}>
                {stats ? formatBodyMetricValue(type, stats.min) : ""}
              </span>
            </div>
            <div className={styles.root_tile}>
              <span className={styles.root_tile_label}>Най-високо</span>
              <span className={styles.root_tile_value}>
                {stats ? formatBodyMetricValue(type, stats.max) : ""}
              </span>
            </div>
          </div>

          <RenderWhen condition={changes.length > 0}>
            <section className={styles.root_card}>
              <h3 className={styles.root_card_title}>Промяна между измерванията</h3>
              <div className={classNames(styles.root_card_canvas, styles.root_card_canvas_short)}>
                <BodyMetricChangesChart type={type} changes={changes} />
              </div>
            </section>
          </RenderWhen>
        </RenderWhen>

        <section className={styles.root_history}>
          <h3 className={styles.root_card_title}>Всички стойности</h3>
          {readings.map(({ entry, change }) => (
            <div key={entry.id} className={styles.root_history_row}>
              <div className={styles.root_history_main}>
                <span className={styles.root_history_value}>
                  {formatBodyMetricValue(type, entry.value)}
                </span>
                <span className={styles.root_history_meta}>
                  {dateUtility.formatDate(entry.measuredOn)}
                  {/* No author once whoever recorded it was deleted forever. */}
                  {entry.createdByName ? ` · ${entry.createdByName}` : ""}
                </span>
              </div>
              <RenderWhen condition={change !== null}>
                <span className={styles.root_history_change}>
                  {change !== null ? formatBodyMetricChange(type, change) : ""}
                </span>
              </RenderWhen>
              <button
                type="button"
                className={styles.root_history_delete}
                onClick={() => requestDelete(entry)}
                aria-label={`Изтрий ${formatBodyMetricValue(type, entry.value)} от ${dateUtility.formatDate(entry.measuredOn)}`}
                title="Изтрий"
              >
                <i className="pi pi-trash" aria-hidden="true" />
              </button>
            </div>
          ))}
        </section>
      </div>
    </AppDialog>
  );
};
